<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { onMounted, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { PvEtudeEcoPlageHoraire } from "@modules/pv/types/pv-etude-eco-form.types"
import type { PvEtudeEcoSubscribeForm } from "@modules/pv/types/pv-etude-eco-form.types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            titleBefore: "Abonnement électrique actuel",
            titleAfter: "Nouvel abonnement électrique",
            titleHint: "Vous trouvez ces informations dans votre contrat",
            pricingBase: "Base",
            pricingHphc: "HP/HC",
            pricingTempo: "Tempo",
            placeholder: "----",
            plageTitle: "Plage horaire heures creuses {n}",
            plageStart: "Début",
            plageEnd: "Fin",
            addPlage: "Ajouter une plage horaire",
            removePlage: "Supprimer",
            base_prix_abonnement: "Prix de l’abonnement",
            base_prix_kwh: "Prix du kWh",
            hphc_prix_abonnement: "Prix de l’abonnement",
            hphc_prix_kwh_hp: "Prix du kWh HP",
            hphc_prix_kwh_hc: "Prix du kWh HC",
            tempo_prix_abonnement: "Prix de l’abonnement",
            tempo_prix_buhp: "Bleu HP",
            tempo_prix_buhc: "Bleu HC",
            tempo_prix_bchp: "Blanc HP",
            tempo_prix_bchc: "Blanc HC",
            tempo_prix_rhp: "Rouge HP",
            tempo_prix_rhc: "Rouge HC",
            hs_prix_abonnement: "Prix de l’abonnement",
            hs_prix_kwh_hph: "Prix du kWh HP saison haute",
            hs_prix_kwh_hch: "Prix du kWh HC saison haute",
            hs_prix_kwh_hpb: "Prix du kWh HP saison basse",
            hs_prix_kwh_hcb: "Prix du kWh HC saison basse",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Segment tarifaire du compteur (ex. C5). C4/C2 force le profil « saison haute / basse ». */
        segment?: string
        /** `true` : bloc « avant installation », `false` : « après ». */
        before?: boolean
        /** Erreurs vee-validate (ex. base.prix_abonnement, hphc.plage.0.time_from). */
        fieldErrors?: Record<string, string | undefined>
    }>(),
    {
        segment: "C5",
        before: false,
        fieldErrors: () => ({}),
    }
)

const model = defineModel<PvEtudeEcoSubscribeForm>({ required: true })

// ================================
// LOGIQUE MÉTIER
// ================================
function applySegmentPricing(): void {
    if (["C4", "C2"].includes(props.segment)) {
        model.value.pricing_type = "hs"
    }
}

function onAddPlage(target: "hphc" | "hs"): void {
    const list = model.value[target].plage
    list.push({ time_from: null, time_to: null })
}

function onRemovePlage(target: "hphc" | "hs", index: number): void {
    const list = model.value[target].plage
    if (list.length <= 1) return
    list.splice(index, 1)
}

function getPlageList(target: "hphc" | "hs"): PvEtudeEcoPlageHoraire[] {
    return model.value[target].plage
}

/**
 * Message d’erreur pour un champ du tarif actif (base / hphc / tempo / hs).
 */
function getSliceFieldError(fieldKey: string): string | undefined {
    const pt = model.value.pricing_type
    return props.fieldErrors?.[`${pt}.${fieldKey}`]
}

/**
 * Message d’erreur pour une plage horaire (HP/HC ou saison haute/basse).
 */
function getPlageFieldError(
    target: "hphc" | "hs",
    index: number,
    part: "time_from" | "time_to"
): string | undefined {
    return props.fieldErrors?.[`${target}.plage.${index}.${part}`]
}

// ================================
// WATCHERS
// ================================
watch(
    () => props.segment,
    () => {
        applySegmentPricing()
    }
)

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    applySegmentPricing()
})
</script>

<template>
    <div class="pv-etude-eco-form-subscribe flex flex-col gap-4">
        <div class="flex flex-wrap items-center gap-2">
            <h3 class="text-base font-semibold text-primary-900">
                {{ before ? t("titleBefore") : t("titleAfter") }}
            </h3>
            <span
                v-tooltip.top="t('titleHint')"
                class="pi pi-info-circle text-primary-600 text-sm cursor-help"
                aria-hidden="true"
            />
        </div>
        <div
            v-if="!['C4', 'C2'].includes(segment)"
            class="flex flex-wrap gap-4"
        >
            <div class="flex items-center gap-2">
                <BaseRadioButton
                    :id="`pv-eco-pr-${before ? 'b' : 'a'}-base`"
                    v-model="model.pricing_type"
                    value="base"
                    :name="`pv-eco-pricing-${before ? 'before' : 'after'}`"
                />
                <label :for="`pv-eco-pr-${before ? 'b' : 'a'}-base`">{{
                    t("pricingBase")
                }}</label>
            </div>
            <div class="flex items-center gap-2">
                <BaseRadioButton
                    :id="`pv-eco-pr-${before ? 'b' : 'a'}-hphc`"
                    v-model="model.pricing_type"
                    value="hphc"
                    :name="`pv-eco-pricing-${before ? 'before' : 'after'}`"
                />
                <label :for="`pv-eco-pr-${before ? 'b' : 'a'}-hphc`">{{
                    t("pricingHphc")
                }}</label>
            </div>
            <div class="flex items-center gap-2">
                <BaseRadioButton
                    :id="`pv-eco-pr-${before ? 'b' : 'a'}-tempo`"
                    v-model="model.pricing_type"
                    value="tempo"
                    :name="`pv-eco-pricing-${before ? 'before' : 'after'}`"
                />
                <label :for="`pv-eco-pr-${before ? 'b' : 'a'}-tempo`">{{
                    t("pricingTempo")
                }}</label>
            </div>
        </div>

        <!-- Base -->
        <div
            v-if="model.pricing_type === 'base'"
            class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end"
        >
            <BaseInputWrapper
                :input-id="`pv-eco-base-abo-${before}`"
                :label="t('base_prix_abonnement')"
                suffix="€/mois"
                :error="getSliceFieldError('prix_abonnement')"
            >
                <BaseInputNumber
                    :id="`pv-eco-base-abo-${before}`"
                    v-model="model.base.prix_abonnement"
                    :step="0.01"
                    :max-fraction-digits="4"
                    :placeholder="t('placeholder')"
                    class="w-full"
                    :invalid="Boolean(getSliceFieldError('prix_abonnement'))"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                :input-id="`pv-eco-base-kwh-${before}`"
                :label="t('base_prix_kwh')"
                suffix="cts €/kWh"
                :error="getSliceFieldError('prix_kwh')"
            >
                <BaseInputNumber
                    :id="`pv-eco-base-kwh-${before}`"
                    v-model="model.base.prix_kwh"
                    :step="0.01"
                    :max-fraction-digits="4"
                    :placeholder="t('placeholder')"
                    class="w-full"
                    :invalid="Boolean(getSliceFieldError('prix_kwh'))"
                />
            </BaseInputWrapper>
        </div>

        <!-- HPHC -->
        <template v-else-if="model.pricing_type === 'hphc'">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <BaseInputWrapper
                    :input-id="`pv-eco-hphc-abo-${before}`"
                    :label="t('hphc_prix_abonnement')"
                    suffix="€/mois"
                    :error="getSliceFieldError('prix_abonnement')"
                >
                    <BaseInputNumber
                        :id="`pv-eco-hphc-abo-${before}`"
                        v-model="model.hphc.prix_abonnement"
                        :step="0.01"
                        :max-fraction-digits="4"
                        :placeholder="t('placeholder')"
                        class="w-full"
                        :invalid="
                            Boolean(getSliceFieldError('prix_abonnement'))
                        "
                    />
                </BaseInputWrapper>
                <div
                    class="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 items-end"
                >
                    <BaseInputWrapper
                        :input-id="`pv-eco-hphc-hp-${before}`"
                        :label="t('hphc_prix_kwh_hp')"
                        suffix="cts €/kWh"
                        :error="getSliceFieldError('prix_kwh_hp')"
                    >
                        <BaseInputNumber
                            :id="`pv-eco-hphc-hp-${before}`"
                            v-model="model.hphc.prix_kwh_hp"
                            :step="0.01"
                            :max-fraction-digits="4"
                            :placeholder="t('placeholder')"
                            class="w-full"
                            :invalid="
                                Boolean(getSliceFieldError('prix_kwh_hp'))
                            "
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :input-id="`pv-eco-hphc-hc-${before}`"
                        :label="t('hphc_prix_kwh_hc')"
                        suffix="cts €/kWh"
                        :error="getSliceFieldError('prix_kwh_hc')"
                    >
                        <BaseInputNumber
                            :id="`pv-eco-hphc-hc-${before}`"
                            v-model="model.hphc.prix_kwh_hc"
                            :step="0.01"
                            :max-fraction-digits="4"
                            :placeholder="t('placeholder')"
                            class="w-full"
                            :invalid="
                                Boolean(getSliceFieldError('prix_kwh_hc'))
                            "
                        />
                    </BaseInputWrapper>
                </div>
            </div>
            <div
                class="flex flex-col gap-4 border border-primary-100 rounded-lg p-4"
            >
                <p
                    v-if="getSliceFieldError('plage')"
                    class="text-sm text-red-600"
                >
                    {{ getSliceFieldError("plage") }}
                </p>
                <div
                    v-for="(plage, index) in getPlageList('hphc')"
                    :key="`hphc-plage-${before}-${index}`"
                    class="flex flex-col gap-3"
                >
                    <div
                        class="flex flex-wrap items-center justify-between gap-2"
                    >
                        <p class="text-sm font-medium text-primary-900">
                            {{ t("plageTitle", { n: index + 1 }) }}
                        </p>
                        <BaseButton
                            v-if="getPlageList('hphc').length > 1"
                            :label="t('removePlage')"
                            size="small"
                            variant="text"
                            severity="secondary"
                            @click="onRemovePlage('hphc', index)"
                        />
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <BaseInputWrapper
                            :input-id="`pv-eco-hphc-from-${before}-${index}`"
                            :label="t('plageStart')"
                            :error="
                                getPlageFieldError('hphc', index, 'time_from')
                            "
                        >
                            <input
                                :id="`pv-eco-hphc-from-${before}-${index}`"
                                v-model="plage.time_from"
                                type="time"
                                class="w-full rounded-md border px-3 py-2 text-sm bg-white"
                                :class="
                                    getPlageFieldError(
                                        'hphc',
                                        index,
                                        'time_from'
                                    )
                                        ? 'border-red-500'
                                        : 'border-primary-200'
                                "
                            />
                        </BaseInputWrapper>
                        <BaseInputWrapper
                            :input-id="`pv-eco-hphc-to-${before}-${index}`"
                            :label="t('plageEnd')"
                            :error="
                                getPlageFieldError('hphc', index, 'time_to')
                            "
                        >
                            <input
                                :id="`pv-eco-hphc-to-${before}-${index}`"
                                v-model="plage.time_to"
                                type="time"
                                class="w-full rounded-md border px-3 py-2 text-sm bg-white"
                                :class="
                                    getPlageFieldError('hphc', index, 'time_to')
                                        ? 'border-red-500'
                                        : 'border-primary-200'
                                "
                            />
                        </BaseInputWrapper>
                    </div>
                </div>
                <BaseButton
                    :label="t('addPlage')"
                    icon="pi pi-plus"
                    size="small"
                    variant="outlined"
                    class="self-start"
                    @click="onAddPlage('hphc')"
                />
            </div>
        </template>

        <!-- Tempo -->
        <div
            v-else-if="model.pricing_type === 'tempo'"
            class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end"
        >
            <BaseInputWrapper
                :input-id="`pv-eco-tempo-abo-${before}`"
                :label="t('tempo_prix_abonnement')"
                suffix="€/mois"
                class="sm:col-span-2"
                :error="getSliceFieldError('prix_abonnement')"
            >
                <BaseInputNumber
                    :id="`pv-eco-tempo-abo-${before}`"
                    v-model="model.tempo.prix_abonnement"
                    :step="0.01"
                    :max-fraction-digits="4"
                    :placeholder="t('placeholder')"
                    class="w-full max-w-md"
                    :invalid="Boolean(getSliceFieldError('prix_abonnement'))"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                :input-id="`pv-eco-tempo-buhp-${before}`"
                :label="t('tempo_prix_buhp')"
                suffix="cts €/kWh"
                :error="getSliceFieldError('prix_buhp')"
            >
                <BaseInputNumber
                    :id="`pv-eco-tempo-buhp-${before}`"
                    v-model="model.tempo.prix_buhp"
                    :step="0.01"
                    :max-fraction-digits="4"
                    class="w-full"
                    :invalid="Boolean(getSliceFieldError('prix_buhp'))"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                :input-id="`pv-eco-tempo-buhc-${before}`"
                :label="t('tempo_prix_buhc')"
                suffix="cts €/kWh"
                :error="getSliceFieldError('prix_buhc')"
            >
                <BaseInputNumber
                    :id="`pv-eco-tempo-buhc-${before}`"
                    v-model="model.tempo.prix_buhc"
                    :step="0.01"
                    :max-fraction-digits="4"
                    class="w-full"
                    :invalid="Boolean(getSliceFieldError('prix_buhc'))"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                :input-id="`pv-eco-tempo-bchp-${before}`"
                :label="t('tempo_prix_bchp')"
                suffix="cts €/kWh"
                :error="getSliceFieldError('prix_bchp')"
            >
                <BaseInputNumber
                    :id="`pv-eco-tempo-bchp-${before}`"
                    v-model="model.tempo.prix_bchp"
                    :step="0.01"
                    :max-fraction-digits="4"
                    class="w-full"
                    :invalid="Boolean(getSliceFieldError('prix_bchp'))"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                :input-id="`pv-eco-tempo-bchc-${before}`"
                :label="t('tempo_prix_bchc')"
                suffix="cts €/kWh"
                :error="getSliceFieldError('prix_bchc')"
            >
                <BaseInputNumber
                    :id="`pv-eco-tempo-bchc-${before}`"
                    v-model="model.tempo.prix_bchc"
                    :step="0.01"
                    :max-fraction-digits="4"
                    class="w-full"
                    :invalid="Boolean(getSliceFieldError('prix_bchc'))"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                :input-id="`pv-eco-tempo-rhp-${before}`"
                :label="t('tempo_prix_rhp')"
                suffix="cts €/kWh"
                :error="getSliceFieldError('prix_rhp')"
            >
                <BaseInputNumber
                    :id="`pv-eco-tempo-rhp-${before}`"
                    v-model="model.tempo.prix_rhp"
                    :step="0.01"
                    :max-fraction-digits="4"
                    class="w-full"
                    :invalid="Boolean(getSliceFieldError('prix_rhp'))"
                />
            </BaseInputWrapper>
            <BaseInputWrapper
                :input-id="`pv-eco-tempo-rhc-${before}`"
                :label="t('tempo_prix_rhc')"
                suffix="cts €/kWh"
                :error="getSliceFieldError('prix_rhc')"
            >
                <BaseInputNumber
                    :id="`pv-eco-tempo-rhc-${before}`"
                    v-model="model.tempo.prix_rhc"
                    :step="0.01"
                    :max-fraction-digits="4"
                    class="w-full"
                    :invalid="Boolean(getSliceFieldError('prix_rhc'))"
                />
            </BaseInputWrapper>
        </div>

        <!-- Saison haute / basse -->
        <template v-else>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <BaseInputWrapper
                    :input-id="`pv-eco-hs-abo-${before}`"
                    :label="t('hs_prix_abonnement')"
                    suffix="€/mois"
                    :error="getSliceFieldError('prix_abonnement')"
                >
                    <BaseInputNumber
                        :id="`pv-eco-hs-abo-${before}`"
                        v-model="model.hs.prix_abonnement"
                        :step="0.01"
                        :max-fraction-digits="4"
                        class="w-full"
                        :invalid="
                            Boolean(getSliceFieldError('prix_abonnement'))
                        "
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    :input-id="`pv-eco-hs-hph-${before}`"
                    :label="t('hs_prix_kwh_hph')"
                    suffix="cts €/kWh"
                    :error="getSliceFieldError('prix_kwh_hph')"
                >
                    <BaseInputNumber
                        :id="`pv-eco-hs-hph-${before}`"
                        v-model="model.hs.prix_kwh_hph"
                        :step="0.001"
                        :max-fraction-digits="4"
                        class="w-full"
                        :invalid="Boolean(getSliceFieldError('prix_kwh_hph'))"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    :input-id="`pv-eco-hs-hch-${before}`"
                    :label="t('hs_prix_kwh_hch')"
                    suffix="cts €/kWh"
                    :error="getSliceFieldError('prix_kwh_hch')"
                >
                    <BaseInputNumber
                        :id="`pv-eco-hs-hch-${before}`"
                        v-model="model.hs.prix_kwh_hch"
                        :step="0.001"
                        :max-fraction-digits="4"
                        class="w-full"
                        :invalid="Boolean(getSliceFieldError('prix_kwh_hch'))"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    :input-id="`pv-eco-hs-hpb-${before}`"
                    :label="t('hs_prix_kwh_hpb')"
                    suffix="cts €/kWh"
                    :error="getSliceFieldError('prix_kwh_hpb')"
                >
                    <BaseInputNumber
                        :id="`pv-eco-hs-hpb-${before}`"
                        v-model="model.hs.prix_kwh_hpb"
                        :step="0.001"
                        :max-fraction-digits="4"
                        class="w-full"
                        :invalid="Boolean(getSliceFieldError('prix_kwh_hpb'))"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    :input-id="`pv-eco-hs-hcb-${before}`"
                    :label="t('hs_prix_kwh_hcb')"
                    suffix="cts €/kWh"
                    :error="getSliceFieldError('prix_kwh_hcb')"
                >
                    <BaseInputNumber
                        :id="`pv-eco-hs-hcb-${before}`"
                        v-model="model.hs.prix_kwh_hcb"
                        :step="0.001"
                        :max-fraction-digits="4"
                        class="w-full"
                        :invalid="Boolean(getSliceFieldError('prix_kwh_hcb'))"
                    />
                </BaseInputWrapper>
            </div>
            <div
                class="flex flex-col gap-4 border border-primary-100 rounded-lg p-4"
            >
                <p
                    v-if="getSliceFieldError('plage')"
                    class="text-sm text-red-600"
                >
                    {{ getSliceFieldError("plage") }}
                </p>
                <div
                    v-for="(plage, index) in getPlageList('hs')"
                    :key="`hs-plage-${before}-${index}`"
                    class="flex flex-col gap-3"
                >
                    <div
                        class="flex flex-wrap items-center justify-between gap-2"
                    >
                        <p class="text-sm font-medium text-primary-900">
                            {{ t("plageTitle", { n: index + 1 }) }}
                        </p>
                        <BaseButton
                            v-if="getPlageList('hs').length > 1"
                            :label="t('removePlage')"
                            size="small"
                            variant="text"
                            severity="secondary"
                            @click="onRemovePlage('hs', index)"
                        />
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <BaseInputWrapper
                            :input-id="`pv-eco-hs-from-${before}-${index}`"
                            :label="t('plageStart')"
                            :error="
                                getPlageFieldError('hs', index, 'time_from')
                            "
                        >
                            <input
                                :id="`pv-eco-hs-from-${before}-${index}`"
                                v-model="plage.time_from"
                                type="time"
                                class="w-full rounded-md border px-3 py-2 text-sm bg-white"
                                :class="
                                    getPlageFieldError('hs', index, 'time_from')
                                        ? 'border-red-500'
                                        : 'border-primary-200'
                                "
                            />
                        </BaseInputWrapper>
                        <BaseInputWrapper
                            :input-id="`pv-eco-hs-to-${before}-${index}`"
                            :label="t('plageEnd')"
                            :error="getPlageFieldError('hs', index, 'time_to')"
                        >
                            <input
                                :id="`pv-eco-hs-to-${before}-${index}`"
                                v-model="plage.time_to"
                                type="time"
                                class="w-full rounded-md border px-3 py-2 text-sm bg-white"
                                :class="
                                    getPlageFieldError('hs', index, 'time_to')
                                        ? 'border-red-500'
                                        : 'border-primary-200'
                                "
                            />
                        </BaseInputWrapper>
                    </div>
                </div>
                <BaseButton
                    :label="t('addPlage')"
                    icon="pi pi-plus"
                    size="small"
                    variant="outlined"
                    class="self-start"
                    @click="onAddPlage('hs')"
                />
            </div>
        </template>
    </div>
</template>
